import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { AufgabeDetail } from './AufgabeDetail'

/**
 * **Das Aufgabendetail, einmal für die ganze Anwendung.**
 *
 * ## Warum das nicht so bleiben konnte, wie es war
 *
 * Sechs Ansichten hielten je einen eigenen `useState` und stellten je ein eigenes
 * `<AufgabeDetail>` daneben: Alle Aufgaben, Mein Tag, Pools, Eingang, Rückstand und das
 * Tages-Popup. Das funktionierte – aber es war **sechsmal dieselbe Zusage, sechsmal
 * einzeln gegeben.** Wer eine siebte Ansicht baut, muss daran denken.
 *
 * Genau das ist passiert: der **Leitstand** hat sie nicht bekommen. Nils, 03.08.2026:
 * *„da fehlt noch die Möglichkeit im Leitstand auf den Aufgabentitel zu drücken. Bitte da
 * noch mal ÜBERALL kontrollieren."* Eine Liste durchzugehen behebt den Einzelfall; dass es
 * nur noch **eine** Stelle gibt, behebt die Ursache.
 *
 * ## Und der Nebenbefund
 *
 * Beim Zusammenlegen kam heraus, dass **Esc das Detail nirgends schloss** – obwohl es so
 * in der Abnahmeliste stand. Das Tages-Popup hatte einen Tastaturhänger, das Detail nicht.
 * Jetzt hat er einen, und zwar an genau der Stelle, an der das Detail auch entsteht.
 */
interface AufgabeZustand {
  /** Kennung der offenen Aufgabe, oder `null`. */
  offenFuer: string | null
  oeffnen: (aufgabeId: string) => void
  schliessen: () => void
}

const Kontext = createContext<AufgabeZustand | null>(null)

export function AufgabeProvider({ children }: { children: ReactNode }) {
  const [offenFuer, setOffenFuer] = useState<string | null>(null)

  /**
   * **`?aufgabe=<Kennung>` öffnet das Detail** (18.08.2026), **und die Adresse zieht mit**
   * ([E-311](../../../../docs/ENTSCHEIDUNGEN.md#e-311), 02.09.2026).
   *
   * Gebaut für die Erwähnungsmail: eine Benachrichtigung, die nur auf die Startseite zeigt,
   * verlangt vom Empfänger, die Aufgabe selbst zu suchen – bei 150 offenen ist das die Arbeit,
   * die die Mail eigentlich abnehmen sollte.
   *
   * ## Was sich am 02.09.2026 geändert hat – und warum die alte Entscheidung fiel
   *
   * Aus Ticket NCW-6X848M: *„Kann Aufgaben nicht teilen, also per Link"* und *„Aufgabendetails
   * in Tab öffnen können"*. Beides gab es technisch längst – die Adresse oben blieb nur stehen,
   * wo sie war, also konnte man sie nicht kopieren.
   *
   * Die ursprüngliche Begründung dagegen war: *„ein Verlauf, der beim Zurück-Klick etwas
   * anderes zeigt als beim Vorwärts, ist verwirrender als ein Parameter, der stehen bleibt."*
   * Das war die Beschreibung eines **halben** Umbaus. Vollständig gedacht ist der Zurück-Klick
   * gerade das, was man erwartet: er schließt das Fenster, das man geöffnet hat.
   *
   * ## Die drei Zeilen, auf die es dabei ankommt
   *
   * 1. **Öffnen schiebt einen Eintrag in den Verlauf** (`pushState`) – sonst gäbe es nichts,
   *    wohin „zurück" führen könnte.
   * 2. **Schließen geht über `history.back()`**, wenn wir den Eintrag selbst geschoben haben.
   *    Ein `replaceState` beim Schließen ließe den Verlauf anwachsen: erst nach dem zweiten
   *    Zurück-Klick käme man wieder auf die Seite davor.
   * 3. **`popstate` schließt das Fenster**, ohne noch einmal am Verlauf zu drehen – sonst
   *    liefen Browser und Anwendung gegeneinander.
   */
  const [ausVerlauf, setAusVerlauf] = useState(false)

  useEffect(() => {
    const gewuenscht = new URLSearchParams(window.location.search).get('aufgabe')
    if (gewuenscht) setOffenFuer(gewuenscht)
  }, [])

  /** Der Zurück-Knopf schließt das Fenster – und nur das, ohne die Seite zu wechseln. */
  useEffect(() => {
    const zurueck = () => {
      const gewuenscht = new URLSearchParams(window.location.search).get('aufgabe')
      setOffenFuer(gewuenscht)
      setAusVerlauf(false)
    }
    window.addEventListener('popstate', zurueck)
    return () => window.removeEventListener('popstate', zurueck)
  }, [])

  const oeffnenMitAdresse = useCallback((aufgabeId: string) => {
    setOffenFuer(aufgabeId)

    const adresse = new URL(window.location.href)
    if (adresse.searchParams.get('aufgabe') === aufgabeId) return
    adresse.searchParams.set('aufgabe', aufgabeId)
    window.history.pushState({ aufgabe: aufgabeId }, '', adresse)
    setAusVerlauf(true)
  }, [])

  const schliessenMitAdresse = useCallback(() => {
    setOffenFuer(null)

    /**
     * **Nur zurückgehen, wenn wir selbst hierher geschoben haben.** Wer über einen Maillink
     * kommt, hat vor dieser Aufgabe nichts in seinem Verlauf – ein `back()` führte ihn aus
     * Work heraus. Dann wird der Parameter still ersetzt.
     */
    if (ausVerlauf) {
      setAusVerlauf(false)
      window.history.back()
      return
    }

    const adresse = new URL(window.location.href)
    if (!adresse.searchParams.has('aufgabe')) return
    adresse.searchParams.delete('aufgabe')
    window.history.replaceState(null, '', adresse)
  }, [ausVerlauf])

  /**
   * **Esc schließt.** Am `window`, nicht am Dialog: der Fokus liegt nach dem Öffnen
   * irgendwo – auf dem Knopf, der ihn geöffnet hat, oder nirgends – und ein Hänger am
   * Element würde die Taste dann nicht sehen.
   */
  useEffect(() => {
    if (!offenFuer) return
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        schliessenMitAdresse()
      }
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [offenFuer, schliessenMitAdresse])

  const wert = useMemo(
    () => ({ offenFuer, oeffnen: oeffnenMitAdresse, schliessen: schliessenMitAdresse }),
    [offenFuer, oeffnenMitAdresse, schliessenMitAdresse],
  )

  return (
    <Kontext.Provider value={wert}>
      {children}
      {offenFuer && <AufgabeDetail aufgabeId={offenFuer} schliessen={schliessenMitAdresse} />}
    </Kontext.Provider>
  )
}

/**
 * Aufgabe öffnen – aus jeder Ansicht, ohne eigenen Zustand.
 *
 * Wirft **nicht**, wenn kein Provider da ist, sondern gibt eine wirkungslose Funktion
 * zurück. Anders als bei der ⌘K-Palette ist das hier richtig: Komponenten wie die
 * Liegezeiten-Matrix werden auch in Tests einzeln gerendert, und ein fehlender Provider
 * soll dort keinen Absturz erzeugen – nur einen Titel, der nicht klickbar ist.
 */
export function useAufgabeOeffnen(): (aufgabeId: string) => void {
  return useContext(Kontext)?.oeffnen ?? NICHTS
}

const NICHTS = () => undefined

/**
 * Steht gerade ein Aufgabendetail offen?
 *
 * Braucht, wer selbst auf Tasten hört: das Tages-Popup hat eine Pfeiltasten-Navigation, und
 * die darf nicht weiterlaufen, während davor ein Dialog steht. Ohne diese Auskunft würde
 * ein Esc beides auf einmal schließen – man käme nie zur Liste zurück.
 */
export function useAufgabeOffen(): boolean {
  return (useContext(Kontext)?.offenFuer ?? null) !== null
}

/**
 * **Welche** Aufgabe offen steht – `null`, wenn keine.
 *
 * Braucht die ⌘K-Palette für „Anpinnen": der Befehl gilt dem, was gerade vor einem liegt
 * ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md)). `useAufgabeOffen` beantwortet
 * nur *ob* – für eine Handlung reicht das nicht.
 */
export function useOffeneAufgabe(): string | null {
  return useContext(Kontext)?.offenFuer ?? null
}

/**
 * **Esc schließt dieses Popup – aber erst, wenn kein Aufgabendetail davor steht**
 * (19.08.2026).
 *
 * Nils: *„wenn man im Planungsfenster ein Popup öffnet, daraus dann eine Aufgabe im Popup –
 * wäre es möglich, dann wieder ins alte Popup zu springen?"*
 *
 * Ja – und der Weg dorthin ist, das Popup **gar nicht erst zu schließen**. Das Detail rendert
 * der `AufgabeProvider` hinter `{children}` und legt sich damit darüber; wer es schließt, sieht
 * die Liste wieder. Das Tages-Popup macht das seit dem 03.08.2026 so, der Trichter schloss sich
 * mit – und man kam nie zur Liste zurück.
 *
 * Die Taste braucht die Rücksicht trotzdem: beide Zuhörer hängen am `window`, und
 * `stopPropagation` hält Zuhörer **am selben Element** nicht auf. Ohne die Abfrage klappte ein
 * Esc beide Ebenen gleichzeitig zu.
 */
export function useEscSchliesst(schliessen: () => void): void {
  const detailOffen = useAufgabeOffen()
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (detailOffen) return
      e.preventDefault()
      schliessen()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [detailOffen, schliessen])
}

/**
 * **Der Aufgabentitel als Knopf** – überall gleich.
 *
 * Nils, 03.08.2026: *„Sonst kann ich den Inhalt nie beurteilen."* Ein Titel in einer Liste
 * ist die einzige Stelle, an der jemand entscheidet, ob eine Aufgabe ihn angeht – und
 * dafür muss er hineinsehen können, ohne die Liste zu verlassen.
 *
 * Sieht bewusst **nicht** wie ein Link aus. Ein blau unterstrichener Titel in einer Liste
 * aus zwanzig Zeilen wäre Lärm; der Hinweis kommt beim Überfahren.
 */
/**
 * **Die Adresse einer Aufgabe** ([E-311](../../../../docs/ENTSCHEIDUNGEN.md#e-311)).
 *
 * Der Pfad bleibt, wie er ist – wer aus „Mein Tag" heraus teilt, teilt einen Link, der wieder
 * in „Mein Tag" landet und das Fenster aufmacht. Nur der Parameter kommt dazu.
 */
export function aufgabenAdresse(aufgabeId: string): string {
  const u = new URL(window.location.href)
  u.searchParams.set('aufgabe', aufgabeId)
  return u.toString()
}

/**
 * **Ein Klick öffnet in Work, Strg+Klick in einem neuen Tab**
 * ([E-319](../../../../docs/ENTSCHEIDUNGEN.md#e-319), 04.09.2026).
 *
 * Davin, in NCW-6X848M: *„Wäre es eventuell noch möglich, Aufgaben per mittlerem Mausrad im
 * neuen Tab zu öffnen? Aktuell geht das dann über Link kopieren + manuell?"* – und Nils dazu:
 * *„Achtung hier nicht das mittlere Mausrad programmieren, sondern dass man das mit Control im
 * neuen Tab öffnet (das mittlere Mausrad macht ja nichts anderes)."*
 *
 * Genau so ist es gebaut, und deshalb steht hier **kein einziger Tastatur-Sonderfall**: Der
 * Titel ist ein echter `<a href>`. Was ein Browser mit einem Verweis alles kann – mittlere
 * Maustaste, Strg, Umschalt, „In neuem Tab öffnen" aus dem Kontextmenü, Ziehen in die
 * Lesezeichen –, kann er ab jetzt auch hier, ohne dass wir es je einzeln nachbauen.
 *
 * ## Wann wir eingreifen und wann nicht
 *
 * Nur beim **einfachen Linksklick ohne Zusatztaste**: dann öffnet das Fenster in Work, ohne die
 * Seite neu zu laden. Sobald eine Zusatztaste im Spiel ist, rühren wir nichts an – das ist die
 * Bewegung, mit der jemand ausdrücklich einen neuen Tab will.
 *
 * Die mittlere Maustaste taucht hier gar nicht auf: Browser lösen dafür `auxclick` aus, nicht
 * `click`. Sie war nie ein Fall, den man behandeln muss – nur einer, den ein `<button>`
 * unmöglich macht.
 */
function ausserhalbBehandeln(e: ReactMouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0
}

/**
 * **Der Verweis auf eine Aufgabe – ohne eine einzige eigene Gestaltungsklasse**
 * (21.09.2026, [E-368](../../../../docs/ENTSCHEIDUNGEN.md#e-368)).
 *
 * `AufgabenTitel` gibt es seit [E-319](../../../../docs/ENTSCHEIDUNGEN.md#e-319), und es
 * brachte seine eigene Optik mit (`text-left`, `truncate`, `break-words`). Genau daran ist die
 * Umstellung damals hängen geblieben: zwei Dutzend Zeilen im Haus haben ihre Klassen selbst
 * abgestimmt – schmale Spalten, Flex-Anteile, eigene Schriftgrößen –, und wer dort `AufgabenTitel`
 * einsetzte, bekam eine zweite Meinung zur Breite dazu. Das ist kein hypothetisches Risiko:
 * [E-320](../../../../docs/ENTSCHEIDUNGEN.md#e-320) ist genau so entstanden, ein Titel, der auf
 * null Pixel schrumpfte.
 *
 * Dieser Baustein nimmt **nur** die Bedienung und lässt jede Klasse durch. Damit ist der Tausch
 * `<button>` → `<a>` an jeder Stelle eine Zeile ohne Nebenwirkung – und es gibt keinen Grund
 * mehr, eine Ansicht auszulassen.
 */
export function AufgabenVerweis({
  aufgabeId,
  className = '',
  title,
  'aria-label': ariaLabel,
  children,
  onClick,
}: {
  aufgabeId: string
  className?: string
  title?: string
  'aria-label'?: string
  /** Optional: der Feed benutzt eine **leere** Fläche als Klickziel (Z-10). */
  children?: ReactNode
  /**
   * **Was noch passieren soll, wenn der Verweis greift** – etwa ein Blatt schließen, aus dem
   * heraus die Aufgabe geöffnet wird (Z-19, 01.10.2026). Läuft **nur** beim schlichten
   * Linksklick; bei Strg-Klick und mittlerer Maustaste öffnet der Browser einen neuen Tab, und
   * dann soll sich hier gar nichts ändern.
   */
  onClick?: () => void
}) {
  const oeffnen = useAufgabeOeffnen()
  return (
    <a
      href={aufgabenAdresse(aufgabeId)}
      onClick={(e) => {
        if (ausserhalbBehandeln(e)) return
        e.preventDefault()
        onClick?.()
        oeffnen(aufgabeId)
      }}
      title={title}
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </a>
  )
}

export function AufgabenTitel({
  aufgabeId,
  titel,
  className = '',
  kuerzen = false,
}: {
  aufgabeId: string
  titel: string
  className?: string
  /** Auf eine Zeile kürzen. Für Tabellen und schmale Spalten. */
  kuerzen?: boolean
}) {
  return (
    <AufgabenVerweis
      aufgabeId={aufgabeId}
      title={titel}
      className={`cursor-pointer text-left hover:underline ${kuerzen ? 'block w-full truncate' : 'break-words'} ${className}`}
    >
      {titel}
    </AufgabenVerweis>
  )
}

/**
 * **Dasselbe für die Fälle, die ihren Titel selbst gestalten** – der Vorlauf und „Auf dem
 * Schirm" bauen eigene Zeilen mit eigenen Klassen, brauchen aber dieselbe Bedienung.
 *
 * Gibt die drei Eigenschaften zurück, statt eine zweite Komponente zu sein: so bleibt die
 * Gestaltung dort, wo sie hingehört, und die Bedienung steht trotzdem nur einmal da.
 */
export function useAufgabenVerweis(aufgabeId: string): {
  href: string
  onClick: (e: ReactMouseEvent) => void
} {
  const oeffnen = useAufgabeOeffnen()
  return {
    href: aufgabenAdresse(aufgabeId),
    onClick: (e) => {
      if (ausserhalbBehandeln(e)) return
      e.preventDefault()
      oeffnen(aufgabeId)
    },
  }
}
